import { Injectable, inject } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { PaymentPlan, PaymentPlanState } from '@models/configuration/model';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs';
import { PaymentPlanStore, PaymentPlanStoreState } from './payment-plan.store';
import { isEligibleForRenewal } from '@app/shared/utils/payment-plan.util';

@Injectable({
  providedIn: 'root',
})
export class PaymentPlanQuery extends QueryEntity<PaymentPlanStoreState> {
  protected store: PaymentPlanStore;

  constructor() {
    const store = inject(PaymentPlanStore);

    super(store);

    this.store = store;
  }

  public selectAllWhenLoaded(): Observable<PaymentPlan[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.selectAll())
    );
  }

  public selectEntityWhenLoaded(id: string): Observable<PaymentPlan> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.selectEntity(id))
    );
  }

  public selectActiveWhenLoaded(): Observable<PaymentPlan> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.selectActive())
    );
  }

  public selectAllActiveWhenLoaded(): Observable<PaymentPlan[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.selectAll()),
      map((plans) => plans.filter((plan) => plan.currentState === PaymentPlanState.Active))
    );
  }

  public getActiveId(): string {
    return super.getActiveId() as string;
  }

  public selectPlanById(id: string): Observable<PaymentPlan> {
    return this.selectAll().pipe(map((paymentPlans) => paymentPlans.find((plan) => plan.id === id)));
  }

  public selectPlansWithinRenewalLeadTime(): Observable<PaymentPlan[]> {
    return this.selectAllActiveWhenLoaded().pipe(map((plans) => plans.filter(isEligibleForRenewal)));
  }

  public selectActivePlansNotDueForRenewal(): Observable<PaymentPlan[]> {
    return this.selectAllActiveWhenLoaded().pipe(map((plans) => plans.filter((plan) => !isEligibleForRenewal(plan))));
  }

  public selectHasPlansWithinRenewalLeadTime(): Observable<boolean> {
    return this.selectPlansWithinRenewalLeadTime().pipe(map((plans) => Boolean(plans.length)));
  }
}
